import React, { useMemo, useState } from 'react';

import { SignalStrengthHelper } from "../../helpers";
import { EntityGroupingAMTDetail } from '../Grouping';
import { CheckboxInput } from '../CheckboxInput';
import MonitoringInfrastructureTable from './monitoring_infrastructure_table';
import i18next from "i18next";

const VALID_HEALTH_VALUES = ['rssi', 'lqi', 'lora_snr'];

const MonitoringInfrastructureView = ({ grouping, entities }) => {
  const [showAllConnections, setShowAllConnections] = useState(true);
  const [healthIndicator, setHealthIndicator] = useState(VALID_HEALTH_VALUES[0]);
  const [deviceOnlyEntities, setDeviceOnlyEntities] = useState([]);
  const [deviceFilter, setDeviceFilter] = useState(null);
  const [connectionLines, setConnectionLines] = useState([]);

  const createConnectionLines = (gateways, gatewaySerialToDeviceMap, deviceFilter) => {
    return Object.entries(gatewaySerialToDeviceMap)
        .flatMap(([gatewaySerial, sensors]) => {
          const gateway = gateways.find((gateway) => gateway.serialNumber === gatewaySerial);
          if (gateway) {
            return sensors.map((sensor) => createLineBetween(gateway, sensor));
          } else {
            console.error(`Could not find gateway for ${gatewaySerial}`);
            return null;
          }
        })
        .filter((connectionLine) => connectionLine != null)
        .filter((connectionLine) => {
          if (deviceFilter != null) {
            const gatewayDeviceFilter = gateways.find((gateway) => gateway.serialNumber === deviceFilter.serialNumber);
            if (gatewayDeviceFilter != null) {
              return connectionLine.props.gwsn === deviceFilter.serialNumber;
            } else {
              return connectionLine.props.ssn === deviceFilter.serialNumber;
            }
          } else {
            return true;
          }
        });
  };

  const createLineBetween = (gateway, sensor) => {
    if (gateway.floorPlanX != null && gateway.floorPlanY != null && sensor.floorPlanX != null && sensor.floorPlanY != null) {
      const x1 = gateway.floorPlanX + 12;
      const y1 = gateway.floorPlanY + 12;
      const x2 = sensor.floorPlanX + 12;
      const y2 = sensor.floorPlanY + 12;

      const lineStyle = {
        borderTop: `2px solid ${getHealthRating(sensor)}`, 
        position: 'absolute', 
        top: y1, 
        left: x1, 
        width: Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1)), 
        zIndex: 1, 
        transform: `rotate(${Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI}deg)`, 
        transformOrigin: '0% 0%'
      };

      return <div key={sensor.serialNumber} style={lineStyle} gwsn={gateway.serialNumber} ssn={sensor.serialNumber} />;
    } else {
      return null;
    }
  };

  const getHealthRating = (device) => {
    switch (SignalStrengthHelper.renderRating(healthIndicator, device)) {
      case 'red': 
        return 'rgb(255, 0, 0)';
      case 'yellow':
        return 'rgb(255, 255, 0)';
      case 'green':
        return 'rgb(0, 255, 0)';
      default:
        return 'rgb(0, 0, 0)';
    }
  };

  useMemo(() => {
    if (entities) {
      const deviceOnlyEntities = entities.filter((entity) => entity.entityTypeToken == "device");

      const gatewayDeviceEntities = deviceOnlyEntities.filter((entity) => entity.entityDisposition == "gateway");

      const gatewayToDeviceMap = gatewayDeviceEntities.reduce((map, gateway) => {
        map[gateway.serialNumber] = deviceOnlyEntities.filter((device) => {
          const loraDevice = device.currentMetadata.find((metadata) => metadata.metadataTypeToken === "gateway_id");
          if (loraDevice) {
            return gateway.serialNumber.endsWith(loraDevice.stringValue);
          }
          const zigbeeDevice = device.currentMetadata.find((metadata) => metadata.metadataTypeToken === "gateway_mac");
          if (zigbeeDevice) {
            return gateway.serialNumber.endsWith(zigbeeDevice.stringValue);
          }
          return false;
        });
        return map;
      }, {});
      
      setDeviceOnlyEntities(deviceOnlyEntities);

      if (showAllConnections) {
        setConnectionLines(createConnectionLines(gatewayDeviceEntities, gatewayToDeviceMap, deviceFilter));
      } else {
        setConnectionLines([]);
      }
    }
  }, [grouping, entities, healthIndicator, showAllConnections, deviceFilter]);

  const generateConnectionsForSelected = (sensor) => {
    if (deviceFilter?.serialNumber === sensor.serialNumber) {
      setDeviceFilter(null);
    } else {
      setDeviceFilter(sensor);
    }
  };

  return (
    <div>
        <div className="monitoring-location-map">
            <div className="map-filter-wrapper">
                <div className="row">
                    <div className="col-sm-10 push-right">
                        <CheckboxInput input={{ checked: showAllConnections, onChange: () => setShowAllConnections(!showAllConnections) }} label="Show Connections" className="inline" meta={{}} />
                    </div>
                    <div className="col-sm-2 push-right text-left">
                    {
                      showAllConnections && VALID_HEALTH_VALUES.map((healthTypeToken) => (
                        <label key={healthTypeToken}>
                            <input className="inline" type="radio" checked={healthIndicator === healthTypeToken} onChange={() => setHealthIndicator(healthTypeToken)} />
                            {i18next.t(`form-labels.${healthTypeToken}`)}
                        </label>
                      ))
                    }
                    </div>
                </div>
            </div>
            <div style={{ marginTop: 15 }}>
                <EntityGroupingAMTDetail grouping={grouping} entities={deviceOnlyEntities} connectionLines={connectionLines} onEntityClick={generateConnectionsForSelected} />
            </div>
            <div>
                <h3>{i18next.t("pages.infrastructure.infrastructure-details")}</h3>
                <MonitoringInfrastructureTable grouping={grouping} data={deviceOnlyEntities} />
            </div>
        </div>
    </div>
  );
}

export default MonitoringInfrastructureView;
